import { Checkbox } from "@package/subframe/components/Checkbox";
import { CheckboxGroup } from "@package/subframe/components/CheckboxGroup";

import { EventLifecycleRole } from "@/generated/model/eventLifecycleRole";
import type { NurseryEventTypeConfigResponseDto } from "@/generated/model/nurseryEventTypeConfigResponseDto";
import {
  EVENT_LIFECYCLE_ROLE_ORDER,
  eventLifecycleRoleLabel,
  type EventTypeRulesDraft,
  rowValidationError,
} from "@/lib/event-records/acknowledgement-rules";

interface EventTypeRulesCardProps {
  config: NurseryEventTypeConfigResponseDto;
  draft: EventTypeRulesDraft;
  disabled: boolean;
  onToggleAcknowledger: (role: EventLifecycleRole) => void;
  onToggleResolver: (role: EventLifecycleRole) => void;
}

/**
 * One event type's acknowledgement rules (NAU-342 AC 4).
 *
 * Two checkbox groups of three fixed options — `CheckboxGroup`'s `label`,
 * `horizontal`, `helpText` and `error` props are exactly what this needs, and this
 * is its first use in the app. A popover multi-select would hide options that
 * should all be visible at a glance, and the checklist schedule's two-column grid
 * exists to wrap an unbounded category list rather than three known roles.
 */
export function EventTypeRulesCard({
  config,
  draft,
  disabled,
  onToggleAcknowledger,
  onToggleResolver,
}: EventTypeRulesCardProps) {
  const error = rowValidationError(draft);

  return (
    <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-secondary-beige-200 p-6">
      <div className="flex w-full flex-col items-start gap-1">
        <span className="text-heading-3 font-heading-3 text-default-font">
          {config.eventTypeName}
        </span>
        {config.isDefault && (
          <span className="text-caption font-caption text-subtext-color">
            Using the platform default — save to set your own.
          </span>
        )}
      </div>

      <div className="flex w-full flex-col items-start gap-4 md:flex-row md:gap-8">
        <CheckboxGroup
          className="w-full md:w-auto"
          label="Who must acknowledge"
          helpText="Their sign-off moves the event on and clears it from Awaiting acknowledgement."
          horizontal
          error={!!error && draft.acknowledgerRoles.length === 0}
        >
          {EVENT_LIFECYCLE_ROLE_ORDER.map((role) => (
            <Checkbox
              key={`ack-${role}`}
              label={eventLifecycleRoleLabel(role)}
              checked={draft.acknowledgerRoles.includes(role)}
              disabled={disabled}
              onCheckedChange={() => onToggleAcknowledger(role)}
            />
          ))}
        </CheckboxGroup>

        <CheckboxGroup
          className="w-full md:w-auto"
          label="Who can mark resolved"
          helpText="Closing the event. Also decides whose own reports skip acknowledgement."
          horizontal
          error={!!error && draft.resolverRoles.length === 0}
        >
          {EVENT_LIFECYCLE_ROLE_ORDER.map((role) => (
            <Checkbox
              key={`resolve-${role}`}
              label={eventLifecycleRoleLabel(role)}
              checked={draft.resolverRoles.includes(role)}
              disabled={disabled}
              onCheckedChange={() => onToggleResolver(role)}
            />
          ))}
        </CheckboxGroup>
      </div>

      {error && (
        <span className="text-body font-body text-error-600">{error}</span>
      )}
    </div>
  );
}
